import { useContext, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import { UserContext } from "../../providers/UserProvider";
import { sendProgramFeedback } from "../../app/api";

type Props = {
  programId?: string;
  slotId?: string;
};

/** Хөтөлбөрт санал өгөх — урсгалын D эгнээ (амрагч хүүхэд / эцэг эх). */
export const ProgramFeedbackForm = ({ programId, slotId }: Props) => {
  const { t } = useTranslation();
  const { userBio } = useContext(UserContext);

  const [rating, setRating] = useState<number>(0);
  const [hovered, setHovered] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  if (!programId) return null;

  // Нэвтрээгүй бол зөвхөн урих
  if (!userBio) {
    return (
      <section className="mt-12 border-t border-neutral-200 pt-8">
        <h2 className="text-2xl font-extrabold text-neutral-900 mb-2">{t('programFeedback.title')}</h2>
        <p className="text-neutral-500">
          {t('programFeedback.loginPrompt')}{' '}
          <Link to="/login" className="text-primary-600 font-semibold hover:underline">
            {t('programFeedback.login')}
          </Link>
        </p>
      </section>
    );
  }

  if (sent) {
    return (
      <section className="mt-12 border-t border-neutral-200 pt-8">
        <h2 className="text-2xl font-extrabold text-neutral-900 mb-2">{t('programFeedback.title')}</h2>
        <p className="text-secondary-600 font-semibold">{t('programFeedback.thanks')}</p>
      </section>
    );
  }

  const submit = async () => {
    setError(undefined);
    if (rating === 0 && !comment.trim()) {
      setError(t('programFeedback.emptyError'));
      return;
    }
    setSending(true);
    try {
      const body: any = { program_id: programId };
      if (slotId) body.slot_id = slotId;
      if (rating > 0) body.rating = rating;
      if (comment.trim()) body.comment = comment.trim();

      const resp: any = await sendProgramFeedback(body);
      if (!resp || !resp.data) {
        setError(t('programFeedback.networkError'));
        return;
      }
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? t('programFeedback.networkError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-12 border-t border-neutral-200 pt-8">
      <h2 className="text-2xl font-extrabold text-neutral-900 mb-1">{t('programFeedback.title')}</h2>
      <p className="text-neutral-500 mb-5">{t('programFeedback.subtitle')}</p>

      {/* Үнэлгээ */}
      <div className="flex items-center gap-1 mb-4" role="group" aria-label={t('programFeedback.rating')}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`${n} / 5`}
            aria-pressed={rating === n}
            onClick={() => setRating(n === rating ? 0 : n)}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            className="p-1 text-3xl leading-none transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded"
          >
            <span className={(hovered || rating) >= n ? 'text-accent-500' : 'text-neutral-300'}>★</span>
          </button>
        ))}
        {rating > 0 && <span className="ml-2 text-sm text-neutral-500">{rating} / 5</span>}
      </div>

      <label htmlFor="program-feedback-comment" className="block text-sm font-semibold text-neutral-700 mb-1">
        {t('programFeedback.comment')}
      </label>
      <textarea
        id="program-feedback-comment"
        value={comment}
        maxLength={2000}
        onChange={(e) => setComment(e.target.value)}
        rows={4}
        placeholder={t('programFeedback.commentPlaceholder')}
        className="w-full px-4 py-3 rounded-xl border-2 border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400 focus:ring-4 focus:ring-primary-200 focus:border-primary-400 transition-all duration-200"
      />

      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-600 font-semibold">{error}</p>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={sending}
        className="mt-4 px-8 py-3 rounded-full font-bold bg-primary-600 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
      >
        {sending ? t('programFeedback.sending') : t('programFeedback.submit')}
      </button>
    </section>
  );
};

export default ProgramFeedbackForm;
